/**
 * Navegador de Google Drive: carpetas y Sheets del usuario (OAuth) o del motor.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ink, studio, SearchField } from '../studioUi';
import { engineFetch, engineErrorMessage, PYTHON_ENGINE_URL } from './engineConfig';
import { auth } from '../../../firebaseConfig';
import { driveListFiles } from './googleDriveClient';

function formatModified(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function DriveSpreadsheetPicker({ open, onClose, onSelect, usedIds = [] }) {
    const [query, setQuery] = useState('');
    const [files, setFiles] = useState([]);
    const [shareEmail, setShareEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pickedId, setPickedId] = useState('');
    const [place, setPlace] = useState('my_drive');
    const [path, setPath] = useState([{ id: 'root', name: 'Mi unidad' }]);
    const [oauthClientId, setOauthClientId] = useState('');
    const [useUserDrive, setUseUserDrive] = useState(false);
    const [listingAs, setListingAs] = useState('');
    const debounceRef = useRef(null);
    const searchSeq = useRef(0);

    const folderId = path[path.length - 1]?.id || 'root';
    const currentDriveId = path.find((crumb) => crumb.driveId)?.driveId || '';
    const searching = query.trim().length > 0;

    const runEngineBrowse = async (term, nextPlace, nextFolder, nextDriveId) => {
        const response = await engineFetch('/api/engine/drive/browse', {
            method: 'POST',
            body: JSON.stringify({
                query: term,
                place: nextPlace,
                folder_id: nextFolder,
                drive_id: nextDriveId || '',
                page_size: 60,
            }),
        });
        if (!response.ok) {
            throw new Error(await engineErrorMessage(response, 'No se pudo abrir Drive.'));
        }
        return response.json();
    };

    const loadConfig = useCallback(async () => {
        const user = auth.currentUser;
        const token = user ? await user.getIdToken() : '';
        const response = await fetch(`${PYTHON_ENGINE_URL}/api/engine/drive/config`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const payload = await response.json().catch(() => ({}));
        return String(payload.clientId || process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
    }, []);

    const runSearch = useCallback(async (term, nextPlace, nextFolder, preferUser, nextDriveId) => {
        const seq = ++searchSeq.current;
        setLoading(true);
        setError('');
        try {
            let payload;
            if (preferUser) {
                payload = await driveListFiles(oauthClientId, {
                    query: term,
                    place: nextPlace,
                    folderId: nextFolder,
                    driveId: nextDriveId,
                });
            } else {
                payload = await runEngineBrowse(term, nextPlace, nextFolder, nextDriveId);
            }
            if (seq !== searchSeq.current) return;
            setFiles(payload.files || []);
            if (payload.share_email) setShareEmail(payload.share_email);
            setListingAs(payload.listing_as || '');
            setUseUserDrive(Boolean(payload.impersonated) || preferUser);
        } catch (err) {
            if (seq !== searchSeq.current) return;
            setFiles([]);
            setError(err.message || 'No se pudo abrir Drive.');
        } finally {
            if (seq === searchSeq.current) setLoading(false);
        }
    }, [oauthClientId]);

    useEffect(() => {
        if (!open) return undefined;
        setQuery('');
        setPickedId('');
        setPlace('my_drive');
        setPath([{ id: 'root', name: 'Mi unidad' }]);
        setError('');
        let cancelled = false;
        (async () => {
            try {
                const clientId = await loadConfig();
                if (!cancelled) setOauthClientId(clientId);
            } catch (_err) {
                if (!cancelled) setOauthClientId(String(process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim());
            }
        })();
        return () => { cancelled = true; };
    }, [open, loadConfig]);

    useEffect(() => {
        if (!open) return undefined;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            runSearch(query, place, folderId, useUserDrive, currentDriveId);
        }, 280);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query, open, place, folderId, currentDriveId, oauthClientId, useUserDrive, runSearch]);

    if (!open) return null;

    const chooseSheet = (file) => {
        setPickedId(file.id);
        onSelect(file);
        onClose();
    };

    const openFolder = (file) => {
        setQuery('');
        if (file.kind === 'shared_drive') {
            setPlace('shared_drives');
            setPath([
                { id: 'shared_root', name: 'Unidades compartidas' },
                { id: file.id, name: file.name, driveId: file.id },
            ]);
            return;
        }
        setPath((prev) => [...prev, { id: file.id, name: file.name, driveId: file.driveId || currentDriveId }]);
    };

    const jumpTo = (index) => {
        setQuery('');
        setPath((prev) => prev.slice(0, index + 1));
    };

    const goPlace = (next) => {
        setQuery('');
        setPlace(next);
        if (next === 'my_drive') setPath([{ id: 'root', name: 'Mi unidad' }]);
        if (next === 'shared_with_me') setPath([{ id: 'shared_with_me', name: 'Compartidos conmigo' }]);
        if (next === 'shared_drives') setPath([{ id: 'shared_root', name: 'Unidades compartidas' }]);
    };

    const connectMine = () => {
        setUseUserDrive(true);
    };

    return (
        <div style={ui.overlay} onClick={onClose} role="presentation">
            <div
                style={ui.sheet}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Buscar en Google Drive"
            >
                <p style={studio.kicker}>Google Drive</p>
                <h3 style={{ ...studio.formTitle, margin: '6px 0 0' }}>Elegir hoja de cálculo</h3>
                <p style={{ ...studio.hint, marginTop: 8, lineHeight: 1.5 }}>
                    {useUserDrive
                        ? 'Navega Mi unidad, Compartidos conmigo o Unidades compartidas. Al elegir un Sheet se leen las pestañas con tu Google; si el motor no tiene acceso, vuelve a conectar y acepta Hojas de cálculo.'
                        : 'Ahora mismo ves lo compartido con el motor. Conecta tu Google para ver tu Drive, las unidades compartidas y las pestañas de cada Sheet.'}
                    {shareEmail ? <> Cuenta del motor: <strong style={{ color: ink.text }}>{shareEmail}</strong>.</> : null}
                </p>

                <div style={ui.toolbar}>
                    <button
                        type="button"
                        onClick={() => goPlace('my_drive')}
                        style={place === 'my_drive' && !searching ? studio.primaryBtn : studio.ghostBtn}
                    >
                        Mi unidad
                    </button>
                    <button
                        type="button"
                        onClick={() => goPlace('shared_with_me')}
                        style={place === 'shared_with_me' && !searching ? studio.primaryBtn : studio.ghostBtn}
                    >
                        Compartidos conmigo
                    </button>
                    <button
                        type="button"
                        onClick={() => goPlace('shared_drives')}
                        style={place === 'shared_drives' && !searching ? studio.primaryBtn : studio.ghostBtn}
                    >
                        Unidades compartidas
                    </button>
                    {!useUserDrive && (
                        <button type="button" onClick={connectMine} style={studio.primaryBtn}>
                            Conectar mi Drive
                        </button>
                    )}
                </div>

                {(place === 'my_drive' || place === 'shared_drives') && !searching && (
                    <div style={ui.crumbs}>
                        {path.map((crumb, index) => (
                            <span key={`${crumb.id}-${index}`}>
                                {index > 0 ? <span style={ui.crumbSep}>/</span> : null}
                                <button type="button" onClick={() => jumpTo(index)} style={studio.crumbBtn}>
                                    {crumb.name}
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                <div style={{ marginTop: 12 }}>
                    <SearchField
                        value={query}
                        onChange={setQuery}
                        placeholder="Buscar por nombre (incluye unidades compartidas)…"
                        style={{ marginBottom: 0 }}
                    />
                </div>
                <div style={ui.list} role="listbox" aria-label="Archivos de Drive">
                    {loading && !files.length ? (
                        <p style={ui.empty}>Cargando Drive…</p>
                    ) : error ? (
                        <p style={ui.error}>{error}</p>
                    ) : files.length === 0 ? (
                        <p style={ui.empty}>
                            {useUserDrive
                                ? 'No hay carpetas ni Sheets aquí.'
                                : `No hay hojas visibles. Conecta tu Drive o comparte el archivo con ${shareEmail || 'la cuenta del motor'}.`}
                        </p>
                    ) : (
                        files.map((file) => {
                            const active = file.id === pickedId;
                            const used = usedIds.includes(file.id);
                            const isFolder = file.kind === 'folder' || file.kind === 'shared_drive';
                            const kindLabel = file.kind === 'shared_drive' ? 'Unidad' : (isFolder ? 'Carpeta' : 'Sheets');
                            return (
                                <button
                                    key={file.id}
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    onClick={() => (isFolder ? openFolder(file) : chooseSheet(file))}
                                    style={{
                                        ...ui.row,
                                        borderColor: active ? ink.accent : ink.line,
                                        backgroundColor: active ? ink.accentSoft : ink.surface,
                                    }}
                                >
                                    <span style={ui.fileName}>
                                        <span style={ui.kind}>{kindLabel}</span>
                                        {file.name}
                                        {used ? <span style={ui.used}>En uso</span> : null}
                                    </span>
                                    <span style={ui.fileMeta}>
                                        {[file.owner, formatModified(file.modifiedTime), listingAs && !useUserDrive ? 'Motor' : '']
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </span>
                                </button>
                            );
                        })
                    )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                    <button type="button" onClick={onClose} style={studio.ghostBtn}>Cerrar</button>
                </div>
            </div>
        </div>
    );
}

const ui = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.45)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
    },
    sheet: {
        ...studio.card,
        width: 'min(720px, 100%)',
        maxHeight: 'min(84vh, 800px)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(28, 29, 33, 0.22)',
    },
    toolbar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 14,
    },
    crumbs: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 4,
        marginTop: 12,
    },
    crumbSep: {
        color: ink.faint,
        margin: '0 4px',
    },
    list: {
        marginTop: 12,
        overflowY: 'auto',
        minHeight: 180,
        maxHeight: 420,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        paddingRight: 2,
    },
    row: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        width: '100%',
        textAlign: 'left',
        padding: '12px 14px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 12,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    fileName: {
        fontSize: '0.95rem',
        fontWeight: 600,
        color: ink.text,
        lineHeight: 1.3,
    },
    kind: {
        display: 'inline-block',
        marginRight: 8,
        fontSize: '0.68rem',
        fontWeight: 800,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: ink.accent,
    },
    used: {
        marginLeft: 8,
        fontSize: '0.7rem',
        fontWeight: 800,
        color: ink.accent,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    fileMeta: {
        fontSize: '0.75rem',
        color: ink.faint,
        fontWeight: 500,
    },
    empty: {
        margin: '28px 8px',
        textAlign: 'center',
        color: ink.muted,
        fontSize: '0.9rem',
        lineHeight: 1.5,
    },
    error: {
        margin: '28px 8px',
        textAlign: 'center',
        color: ink.danger,
        fontSize: '0.9rem',
        lineHeight: 1.5,
    },
};
